import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Draggable from "react-draggable";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

const formatValor = (valor) =>
  Number(valor || 0).toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatarMes = (mes) => {
  if (!mes || !/^\d{4}-\d{2}$/.test(String(mes))) {
    return mes || "";
  }

  const [ano, numeroMes] = String(mes).split("-").map(Number);

  const texto = new Date(ano, numeroMes - 1, 1).toLocaleDateString("pt-PT", {
    month: "long",
    year: "numeric",
  });

  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const formatarData = (valor) => {
  if (!valor) return "";

  const dataISO = String(valor).substring(0, 10);
  const partes = dataISO.split("-");

  if (partes.length !== 3) {
    return dataISO;
  }

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

const obterTituloOrigem = (origem) => {
  switch (String(origem || "").toUpperCase()) {
    case "CAMPANHAS":
      return "Campanhas";

    case "INCENTIVOS":
      return "Incentivos";

    case "DESCONTOS_CLIENTE":
      return "Descontos Cliente";

    case "RECEBIMENTOS":
      return "Recebimentos";

    default:
      return "Detalhe";
  }
};

function PaperComponent(props) {
  return (
    <Draggable
      handle="#dialog-conta-corrente-detalhe-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

PaperComponent.propTypes = {
  children: PropTypes.node,
};

PaperComponent.defaultProps = {
  children: null,
};

export default function DialogContaCorrenteDetalhe({ open, onClose, origem, mes, linhas }) {
  const origemNormalizada = String(origem || "").toUpperCase();

  const abrirCampanha = (linha) => {
    const idcampanha = Number(linha?.id || 0);

    if (!idcampanha || !/^\d{4}-\d{2}$/.test(String(mes || ""))) {
      return;
    }

    const [ano, numeroMes] = String(mes).split("-").map(Number);

    const datainicial = `${ano}-${String(numeroMes).padStart(2, "0")}-01`;

    const ultimoDia = new Date(ano, numeroMes, 0).getDate();

    const datafinal = `${ano}-${String(numeroMes).padStart(2, "0")}-${String(ultimoDia).padStart(
      2,
      "0"
    )}`;

    const params = new URLSearchParams({
      ccCampanha: String(idcampanha),
      datainicial,
      datafinal,
    });

    window.open(
      `${window.location.origin}/comercial/campanhas?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const abrirIncentivo = (linha) => {
    const idincentivo = Number(linha?.id || 0);

    if (!idincentivo || !/^\d{4}-\d{2}$/.test(String(mes || ""))) {
      return;
    }

    const [ano, numeroMes] = String(mes).split("-").map(Number);

    const datainicial = `${ano}-${String(numeroMes).padStart(2, "0")}-01`;

    const ultimoDia = new Date(ano, numeroMes, 0).getDate();

    const datafinal = `${ano}-${String(numeroMes).padStart(2, "0")}-${String(ultimoDia).padStart(
      2,
      "0"
    )}`;

    const params = new URLSearchParams({
      ccIncentivo: String(idincentivo),
      datainicial,
      datafinal,
    });

    window.open(
      `${window.location.origin}/comercial/incentivos?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const abrirDescontoCliente = (linha) => {
    const idacordo = Number(linha?.id || 0);

    if (!idacordo || !/^\d{4}-\d{2}$/.test(String(mes || ""))) {
      return;
    }

    const [ano, numeroMes] = String(mes).split("-").map(Number);

    const datainicial = `${ano}-${String(numeroMes).padStart(2, "0")}-01`;

    const ultimoDia = new Date(ano, numeroMes, 0).getDate();

    const datafinal = `${ano}-${String(numeroMes).padStart(2, "0")}-${String(ultimoDia).padStart(
      2,
      "0"
    )}`;

    const params = new URLSearchParams({
      ccAcordo: String(idacordo),
      datainicial,
      datafinal,
    });

    window.open(
      `${window.location.origin}/comercial/clientes-acordos-materiais?${params.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const dados = useMemo(() => {
    const lista = Array.isArray(linhas) ? linhas : [];

    if (origemNormalizada === "CAMPANHAS") {
      return lista.map((linha, index) => ({
        ...linha,
        _rowKey: `campanha-${linha?.idorigem ?? linha?.reforigem ?? index}`,
        id: linha?.idorigem ?? linha?.reforigem ?? "",
        descricao: linha?.descricao ?? "",
        valorDetalhe: Number(linha?.valor || 0),
      }));
    }

    if (origemNormalizada === "INCENTIVOS") {
      return lista.map((linha, index) => ({
        ...linha,
        _rowKey: `incentivo-${linha?.idorigem ?? linha?.reforigem ?? index}`,
        id: linha?.idorigem ?? linha?.reforigem ?? "",
        descricao: linha?.descricao ?? "",
        valorDetalhe: Number(linha?.valor || 0),
      }));
    }

    if (origemNormalizada === "DESCONTOS_CLIENTE") {
      return lista.map((linha, index) => ({
        ...linha,
        _rowKey: `desconto-${linha?.idacordo ?? index}`,
        id: linha?.idacordo ?? "",
        cliente: linha?.cliente ?? "",
        valorDetalhe: Number(linha?.valor_receber_fabricante || 0),
      }));
    }

    if (origemNormalizada === "RECEBIMENTOS") {
      return lista.map((linha, index) => ({
        ...linha,
        _rowKey: `recebimento-${linha?.id ?? index}`,
        id: linha?.id ?? "",
        dataMovimento: linha?.data_movimento ?? "",
        tipoDocumento: linha?.tipodocumento ?? "",
        documento: linha?.refdocumento ?? "",
        documentoRelacionado: linha?.documentorelacionado ?? "",
        observacoes: linha?.obs ?? "",
        valorDetalhe: Number(linha?.valor || 0),
      }));
    }

    return [];
  }, [linhas, origemNormalizada]);

  const columns = useMemo(() => {
    if (origemNormalizada === "CAMPANHAS") {
      return [
        {
          Header: "",
          id: "abrir",
          disableFilters: true,
          disableSortBy: true,
          filterable: false,
          sortable: false,
          hideable: false,
          resizable: false,
          width: 54,
          minWidth: 54,
          maxWidth: 54,
          Cell: ({ row }) => (
            <IconButton
              size="small"
              color="info"
              title="Abrir resultados da campanha num novo separador"
              onClick={() => abrirCampanha(row.original)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          ),
        },
        {
          Header: "ID",
          accessor: "id",
          width: 90,
          sortType: "numPt",
        },
        {
          Header: "Campanha",
          accessor: "descricao",
          width: 420,
          sortType: "textPt",
        },
        {
          Header: "Valor",
          accessor: "valorDetalhe",
          width: 140,
          sortType: "numPt",
          Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
        },
      ];
    }

    if (origemNormalizada === "INCENTIVOS") {
      return [
        {
          Header: "",
          id: "abrir",
          disableFilters: true,
          disableSortBy: true,
          filterable: false,
          sortable: false,
          hideable: false,
          resizable: false,
          width: 54,
          minWidth: 54,
          maxWidth: 54,
          Cell: ({ row }) => (
            <IconButton
              size="small"
              color="info"
              title="Abrir resultados do incentivo num novo separador"
              onClick={() => abrirIncentivo(row.original)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          ),
        },
        {
          Header: "ID",
          accessor: "id",
          width: 90,
          sortType: "numPt",
        },
        {
          Header: "Incentivo",
          accessor: "descricao",
          width: 420,
          sortType: "textPt",
        },
        {
          Header: "Valor",
          accessor: "valorDetalhe",
          width: 140,
          sortType: "numPt",
          Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
        },
      ];
    }

    if (origemNormalizada === "DESCONTOS_CLIENTE") {
      return [
        {
          Header: "",
          id: "abrir",
          disableFilters: true,
          disableSortBy: true,
          filterable: false,
          sortable: false,
          hideable: false,
          resizable: false,
          width: 54,
          minWidth: 54,
          maxWidth: 54,
          Cell: ({ row }) => (
            <IconButton
              size="small"
              color="info"
              title="Abrir valor a receber do acordo num novo separador"
              onClick={() => abrirDescontoCliente(row.original)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          ),
        },
        {
          Header: "ID",
          accessor: "id",
          width: 90,
          sortType: "numPt",
        },
        {
          Header: "Cliente",
          accessor: "cliente",
          width: 420,
          sortType: "textPt",
        },
        {
          Header: "Valor",
          accessor: "valorDetalhe",
          width: 140,
          sortType: "numPt",
          Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
        },
      ];
    }

    if (origemNormalizada === "RECEBIMENTOS") {
      return [
        {
          Header: "Data",
          accessor: "dataMovimento",
          width: 115,
          sortType: "textPt",
          Cell: ({ value }) => formatarData(value),
        },
        {
          Header: "Tipo",
          accessor: "tipoDocumento",
          width: 80,
          sortType: "textPt",
        },
        {
          Header: "Documento",
          accessor: "documento",
          width: 150,
          sortType: "textPt",
        },
        {
          Header: "Doc. relacionado",
          accessor: "documentoRelacionado",
          width: 190,
          sortType: "textPt",
        },
        {
          Header: "Observações",
          accessor: "observacoes",
          width: 430,
          sortType: "textPt",
        },
        {
          Header: "Valor",
          accessor: "valorDetalhe",
          width: 140,
          sortType: "numPt",
          Cell: ({ value }) => <span style={{ fontWeight: 700 }}>{formatValor(value)} €</span>,
        },
      ];
    }

    return [];
  }, [origemNormalizada, mes]);

  const total = useMemo(
    () => dados.reduce((acc, linha) => acc + Number(linha?.valorDetalhe || 0), 0),
    [dados]
  );

  const tituloOrigem = obterTituloOrigem(origemNormalizada);
  const mesFormatado = formatarMes(mes);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={origemNormalizada === "RECEBIMENTOS" ? "xl" : "md"}
      PaperComponent={PaperComponent}
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100% - 16px)",
            sm: origemNormalizada === "RECEBIMENTOS" ? "calc(100% - 32px)" : "calc(100% - 48px)",
          },
          maxWidth: origemNormalizada === "RECEBIMENTOS" ? 1400 : 900,
          maxHeight: {
            xs: "calc(100% - 16px)",
            sm: "calc(100% - 64px)",
          },
          m: {
            xs: 1,
            sm: 2,
          },
        },
      }}
    >
      <DialogTitle
        id="dialog-conta-corrente-detalhe-title"
        sx={{
          cursor: "move",
          pb: 1,
        }}
      >
        <MDTypography variant="h6">{tituloOrigem}</MDTypography>

        <MDTypography variant="caption" color="text">
          {mesFormatado}
        </MDTypography>
      </DialogTitle>

      <DialogContent dividers>
        <MDBox
          mb={2}
          display="flex"
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={1}
        >
          <MDTypography variant="button" color="text">
            {dados.length} registo(s)
          </MDTypography>

          <MDTypography variant="button" fontWeight="bold">
            Total: {formatValor(total)} €
          </MDTypography>
        </MDBox>

        <MDBox maxWidth="100%" sx={{ overflow: "auto" }}>
          <ProjectTable
            tableKey={`conta-corrente-detalhe-${origemNormalizada.toLowerCase()}`}
            version="1"
            title=""
            subtitle=""
            columns={columns}
            data={dados}
            loading={false}
            emptyMessage="Sem detalhe para apresentar."
            minWidth={origemNormalizada === "RECEBIMENTOS" ? 1250 : 650}
            maxHeight="55vh"
            defaultSortBy={[{ id: "id", desc: false }]}
            showKeepFilters
          />
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" variant="outlined" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

DialogContaCorrenteDetalhe.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  origem: PropTypes.oneOf(["", "CAMPANHAS", "INCENTIVOS", "DESCONTOS_CLIENTE", "RECEBIMENTOS"]),
  mes: PropTypes.string,
  linhas: PropTypes.arrayOf(PropTypes.object),
};

DialogContaCorrenteDetalhe.defaultProps = {
  open: false,
  onClose: () => {},
  origem: "",
  mes: "",
  linhas: [],
};
